import { z } from 'zod';

import { chatUiBase } from '../../shared/schema/base.js';

/**
 * Approval round-trip cards. The DECISION (approve/deny) is a deterministic,
 * button-driven action on the authenticated app channel — never free chat. The
 * `approval_request` card carries ONE session challenge (issued by the agents
 * ConfirmationService, intent `approvals:decide`, ref = conversationId). The
 * shell renders two buttons (Aprovar / Reprovar) plus an optional justification
 * field; tapping echoes back `AppChannelInboundRequest.confirmation` with the
 * challengeId PLUS the chosen `decision` (and optional `justification`). The
 * challenge proves a deliberate act by the authenticated approver (single-use +
 * TTL, anti-injection); the integrator re-authorizes (403 non-approver) and
 * fences exactly-once — the approve-vs-deny choice is the approver's, by design.
 */
const ChatUiApprovalRequestSchema = z.object({
  challengeId: z.string().min(1),
  expiresInMin: z.number().optional(),
  // DISPLAY-ONLY voucher facts (fetched live from the integrator, never
  // LLM-fabricated). Nothing here authorizes the decision.
  voucherCode: z.string(),
  modal: z.string(),
  traveler: z.object({ name: z.string() }),
  approver: z.object({ name: z.string() }).optional(),
  // The shell shows a justification input when true (optional on both actions).
  justificationAllowed: z.boolean().default(true),
});
export type ChatUiApprovalRequest = z.infer<typeof ChatUiApprovalRequestSchema>;

/** Membro `approval_request` do `ChatUiSchema` (discriminatedUnion). */
export const ApprovalRequestBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('approval_request'),
    request: ChatUiApprovalRequestSchema,
  })
  .strict();

const ChatUiApprovalResultSchema = z.object({
  decision: z.enum(['approve', 'deny']),
  status: z.enum(['published', 'failed']),
  // Optional: the deterministic handler builds this card from the confirmed
  // challenge (it has the conversationId, not necessarily the voucher code).
  voucherCode: z.string().optional(),
});
export type ChatUiApprovalResult = z.infer<typeof ChatUiApprovalResultSchema>;

/** Membro `approval_result` do `ChatUiSchema` (discriminatedUnion). Display-only
 * outcome card emitted by the deterministic submit (handler), no interaction. */
export const ApprovalResultBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('approval_result'),
    result: ChatUiApprovalResultSchema,
  })
  .strict();

/**
 * Bulk approval OUTCOME (`approval_bulk_result`) — display-only partial summary
 * (UX-05). The single-voucher `approval_result` carries only decision/status/
 * voucherCode and CANNOT express an approved/failed split across many vouchers,
 * so the bulk path needs a dedicated kind. Built ONLY by the deterministic
 * handler (04-03) from the route summary, never by the LLM; web-chat re-validates
 * the non-negative counts at the render border (T-04-16).
 */
const ChatUiApprovalBulkResultSchema = z.object({
  approved: z.number().int().min(0),
  failed: z.number().int().min(0),
  total: z.number().int().min(0),
  results: z.array(z.object({ voucherId: z.string(), status: z.string() })),
});
export type ChatUiApprovalBulkResult = z.infer<typeof ChatUiApprovalBulkResultSchema>;

/** Membro `approval_bulk_result` do `ChatUiSchema` (discriminatedUnion).
 * Display-only — sem interação. */
export const ApprovalBulkResultBlockSchema = z
  .object({
    ...chatUiBase,
    kind: z.literal('approval_bulk_result'),
    result: ChatUiApprovalBulkResultSchema,
  })
  .strict();
export type ApprovalBulkResultBlock = z.infer<typeof ApprovalBulkResultBlockSchema>;

/**
 * Builder puro do card de resultado em lote — o handler determinístico o monta
 * FORA do graph a partir do resumo da rota. Devolve `[]` em falha de validação
 * (mesma disciplina de `buildApprovalResultCard`), nunca lança.
 */
export function buildApprovalBulkResultCard(
  result: ChatUiApprovalBulkResult,
  id = 'approval_bulk_result',
): ApprovalBulkResultBlock[] {
  const parsed = ApprovalBulkResultBlockSchema.safeParse({
    id,
    kind: 'approval_bulk_result',
    result,
  });
  return parsed.success ? [parsed.data] : [];
}
